import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    BimActKc2OtherCreateInput,
    BimActKc2OtherUpdateInput,
    ConstructionTypeData,
    BimActKc2OtherItemData,
} from 'gql/graphql';
import { BimActKc2OtherStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { IListItem } from 'types/Contract';
import { IconTrash } from 'uikit';
import { newDate, toISOString } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo } from 'react';
import type React from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useWorkOrderOtherPriceList } from '../BimWorkOrderOthersPrice/hooks';
import { useBimActsKC2Other } from '../BimWorkOrderOthersPrice/hooks/useBimActsKC2Other';
import { useBimWorkOrderSeasons } from '../hooks/useBimWorkOrderSeasons';
import { bimActKc2OtherStatusesListItems } from '../types';

import { BimActKc2OtherEditForm } from './BimActKc2OtherEditForm';
import { useBimActKC2Other } from './hooks/useBimActKC2Other';
import { useBimActKC2OtherDefaults } from './hooks/useBimActKC2OtherDefaults';
import { useBimCreateOrUpdateActKC2Other } from './hooks/useBimCreateOrUpdateActKC2Other';
import { useBimIsUniqueActKC2OtherInternalNumber } from './hooks/useBimIsUniqueActKC2OtherInternalNumber';

interface IBimActKc2OtherEdit {
    actKC2Other?: BimActKc2OtherItemData;
    onClose: (isSuccess: boolean) => void;
    isDisabled?: boolean;
    workOrderId?: number;
    constructionType?: ConstructionTypeData;
    selectedObject?: IListItem<number>;
    priceTableApiRef?: React.RefObject<any>;
    refreshProject?: () => void;
}
export const BimActKc2OtherEdit: React.FC<IBimActKc2OtherEdit> = (props) => {
    const {
        actKC2Other,
        onClose,
        workOrderId,
        constructionType,
        selectedObject,
        priceTableApiRef,
        refreshProject,
        isDisabled,
    } = props;

    const actKC2OtherId = actKC2Other?.id;

    const { formState, handleSubmit, control, setValue, watch, setError, clearErrors } = useForm({
        mode: 'onChange',
        defaultValues: {
            constructionType: constructionType?.name,
            objectName: selectedObject?.label,
        } as any,
    });

    useBimIsUniqueActKC2OtherInternalNumber({
        workOrderId,
        actKC2OtherId,
        control,
        clearErrors,
        setError,
    });

    const { bimActKC2OtherData, bimActKC2OtherStatus } = useBimActKC2Other(actKC2OtherId);

    const isReadOnly = useMemo(
        () => bimActKC2OtherData?.bimActKC2Other?.data?.status === BimActKc2OtherStatusEnum.Accepted || isDisabled,
        [bimActKC2OtherData?.bimActKC2Other?.data?.status, isDisabled],
    );

    const actStatus = useWatch({ control, name: 'status' });

    const isStatusOnCheckAndHasNoPlanValues = useMemo(
        () =>
            bimActKC2OtherData?.bimActKC2Other?.data?.hasPlanValues !== undefined &&
            actStatus?.id === BimActKc2OtherStatusEnum.OnCheck &&
            !bimActKC2OtherData?.bimActKC2Other?.data?.hasPlanValues,
        [actStatus?.id, bimActKC2OtherData?.bimActKC2Other?.data],
    );

    const isDeleteButtonDisabled = useMemo(
        () => actStatus?.id !== BimActKc2OtherStatusEnum.InProgress || isDisabled,
        [actStatus?.id, isDisabled],
    );

    const { refreshWorkOrderOtherPriceList } = useWorkOrderOtherPriceList({
        workOrderId,
        workOrderRootObjectId: selectedObject?.workOrderObjectId,
        withEffect: false,
    });

    const { bimActsKC2OtherMakeRequest } = useBimActsKC2Other(workOrderId, selectedObject?.workOrderObjectId);

    const onSuccess = useCallback(() => {
        onClose(true);
        bimActsKC2OtherMakeRequest();
        refreshWorkOrderOtherPriceList?.();
        refreshProject?.();
    }, [bimActsKC2OtherMakeRequest, onClose, refreshProject, refreshWorkOrderOtherPriceList]);

    const { bimCreateActKC2OtherMakeRequest, bimUpdateActKC2OtherMakeRequest, status } =
        useBimCreateOrUpdateActKC2Other(actKC2OtherId, onSuccess, priceTableApiRef);

    const isEditMode = useMemo(() => !!actKC2OtherId, [actKC2OtherId]);

    const { bimWorkOrderSeasonsData, bimWorkOrderSeasonsStatus } = useBimWorkOrderSeasons(workOrderId, true);

    const { bimActKC2OtherDefaultsData, bimActKC2OtherDefaultsStatus } = useBimActKC2OtherDefaults(
        workOrderId,
        !isEditMode,
    );

    const isLoading = useMemo(
        () =>
            bimActKC2OtherStatus === RequestStatus.FETCHING ||
            bimWorkOrderSeasonsStatus === RequestStatus.FETCHING ||
            bimActKC2OtherDefaultsStatus === RequestStatus.FETCHING,
        [bimActKC2OtherDefaultsStatus, bimActKC2OtherStatus, bimWorkOrderSeasonsStatus],
    );

    const btnConfirmLabel = useMemo(() => (isEditMode ? 'Сохранить' : 'Создать'), [isEditMode]);

    const statusItems = useMemo(
        () =>
            isEditMode
                ? bimActKc2OtherStatusesListItems.filter(
                      (item) =>
                          bimActKC2OtherData?.bimActKC2Other?.data?.statusFollowers?.includes(item.id) ||
                          item.id === bimActKC2OtherData?.bimActKC2Other?.data?.status,
                  )
                : [bimActKc2OtherStatusesListItems[0]],
        [bimActKC2OtherData?.bimActKC2Other?.data, isEditMode],
    );

    const seasonsItems = useMemo(
        () =>
            bimWorkOrderSeasonsData?.bimWorkOrderSeasons?.items?.map((item) => ({
                id: item.workOrderSeasonId,
                label: item.year,
            })) || [],
        [bimWorkOrderSeasonsData?.bimWorkOrderSeasons?.items],
    );

    const onCloseLocal = useCallback(() => {
        onClose(false);
    }, [onClose]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseLocal,
        });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onCloseLocal();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onCloseLocal]);

    const onSubmitDialog = useCallback(
        (formData) => {
            if (!constructionType) {
                return;
            }

            const date = toISOString(formData.date);
            const startDate = toISOString(formData.reportingDate[0]);
            const finishDate = toISOString(formData.reportingDate[1]);
            const number = replacerCost(formData.number);

            if (isEditMode) {
                const input: BimActKc2OtherUpdateInput = {
                    date,
                    startDate,
                    finishDate,
                    id: actKC2OtherId,
                    number,
                    status: formData?.status?.id,
                };

                bimUpdateActKC2OtherMakeRequest(input);
            } else {
                const input: BimActKc2OtherCreateInput = {
                    constructionTypeId: constructionType.id,
                    workOrderId,
                    workOrderObjectId: selectedObject?.workOrderObjectId,
                    workOrderSeasonId: formData.season.id,
                    date,
                    startDate,
                    finishDate,
                    number,
                };

                bimCreateActKC2OtherMakeRequest(input);
            }
        },
        [
            actKC2OtherId,
            bimCreateActKC2OtherMakeRequest,
            bimUpdateActKC2OtherMakeRequest,
            constructionType,
            isEditMode,
            selectedObject?.workOrderObjectId,
            workOrderId,
        ],
    );

    const onDeleteClick = useCallback(() => {
        priceTableApiRef?.current?.api?.deleteExternally(actKC2Other, onCloseLocal);
    }, [actKC2Other, onCloseLocal, priceTableApiRef]);

    useEffect(() => {
        if (bimActKC2OtherData?.bimActKC2Other?.data) {
            setValue(
                'season',
                seasonsItems.find(
                    (s) => s.id === bimActKC2OtherData?.bimActKC2Other?.data?.workOrderSeason?.workOrderSeasonId,
                ),
            );

            setValue(
                'status',
                bimActKc2OtherStatusesListItems.find((s) => s.id === bimActKC2OtherData?.bimActKC2Other?.data?.status),
            );

            setValue('number', bimActKC2OtherData?.bimActKC2Other?.data?.number);

            setValue('date', newDate(bimActKC2OtherData?.bimActKC2Other?.data?.date));
            setValue('reportingDate', [
                newDate(bimActKC2OtherData?.bimActKC2Other?.data?.startDate),
                newDate(bimActKC2OtherData?.bimActKC2Other?.data?.finishDate),
            ]);
            setValue('contractor', bimActKC2OtherData?.bimActKC2Other?.data?.contractor);
            setValue('contractorAddress', bimActKC2OtherData?.bimActKC2Other?.data?.contractorAddress);
        }
    }, [bimActKC2OtherData?.bimActKC2Other?.data, seasonsItems, setValue]);

    useEffect(() => {
        if (bimActKC2OtherDefaultsData?.bimActKC2OtherDefaults?.data) {
            setValue(
                'status',
                bimActKc2OtherStatusesListItems.find(
                    (s) => s.id === bimActKC2OtherDefaultsData?.bimActKC2OtherDefaults?.data?.status,
                ),
            );

            setValue('season', undefined);
            setValue('number', undefined);

            setValue('date', undefined);
            setValue('reportingDate', undefined);
            setValue('contractor', bimActKC2OtherDefaultsData?.bimActKC2OtherDefaults?.data?.contractor);
            setValue('contractorAddress', bimActKC2OtherDefaultsData?.bimActKC2OtherDefaults?.data?.contractorAddress);
        }
    }, [bimActKC2OtherDefaultsData?.bimActKC2OtherDefaults?.data, setValue]);

    return (
        <ModalDialog
            title="Реквизиты Акта КС-2"
            isOpen
            isOnClickOutsideDisable
            btnConfirmLabel={btnConfirmLabel}
            style={{ width: 600 }}
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            isLoading={isLoading}
            isCommit={status === RequestStatus.FETCHING}
            btnConfirmDisabled={isDisabled || isStatusOnCheckAndHasNoPlanValues}
            bodyWidth={600}
            leftPanel={
                actKC2OtherId && (
                    <ButtonAlert
                        disabled={isDeleteButtonDisabled}
                        onlyIcon
                        iconRight={IconTrash}
                        iconSize="s"
                        size="s"
                        onClick={onDeleteClick}
                    />
                )
            }
        >
            <BimActKc2OtherEditForm
                watch={watch}
                clearErrors={clearErrors}
                control={control}
                isStatusOnCheckAndHasNoPlanValues={isStatusOnCheckAndHasNoPlanValues}
                seasonsItems={seasonsItems}
                setValue={setValue}
                statusItems={statusItems}
                isReadOnly={isReadOnly}
                isDisabled={isDisabled}
                isEditMode={isEditMode}
                isInactive={!!bimActKC2OtherData?.bimActKC2Other?.data?.isInactive}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

BimActKc2OtherEdit.displayName = 'BimActKc2OtherEdit';
